// src/components/trades/DraftPicksTab.js
import React, { useState, useEffect } from 'react';
import { dynastyTheme } from '../../services/colorService';
import { Calendar, TrendingUp, ArrowRightLeft, Loader, CheckCircle, Circle } from 'lucide-react';
import { leaguesAPI } from '../../services/apiService';

/**
 * DraftPicksTab Component
 * Displays all draft picks owned by the user with ability to select for trading
 * 
 * Features:
 * - Shows picks filtered by league's future_picks_tradeable_years setting
 * - Groups picks by year (2026, 2027, etc.)
 * - Highlights picks acquired via trade (different original owner)
 * - Pick value indicators (early rounds more valuable)
 * - Selectable for adding to trades
 * - Syncs with ProposeTradeTab through parent state
 */
const DraftPicksTab = ({
  leagueId,
  userTeam,
  league,
  selectedPicks = [],
  onPickToggle,
  readOnly = false,
}) => {
  console.log('🚀 DraftPicksTab mounted with props:', { leagueId, userTeam, league });
  
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allTeams, setAllTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [teamsLoading, setTeamsLoading] = useState(true);

  // Get tradeable years from league settings (default 2 years)
  const tradeableYears = league?.league_settings?.future_picks_tradeable_years || 
                         league?.league_settings?.draft_picks_tradeable_years || 
                         2;
  
  const currentYear = league?.current_season;
  const maxTradeableYear = currentYear + tradeableYears;

  // Load all teams when component mounts
  useEffect(() => {
    if (leagueId) {
      loadTeams();
    }
  }, [leagueId]);

  // Load picks when selected team changes
  useEffect(() => {
    if (leagueId && selectedTeamId) {
      loadPicks(selectedTeamId);
    }
  }, [leagueId, selectedTeamId]);

  // Set initial selected team to user's team
  useEffect(() => {
    if (userTeam?.team_id && !selectedTeamId) {
      setSelectedTeamId(userTeam.team_id);
    }
  }, [userTeam]);

  const loadTeams = async () => {
    try {
      setTeamsLoading(true);
      const response = await leaguesAPI.getLeagueTeams(leagueId);
      
      if (response.success && response.teams) {
        // Sort teams alphabetically
        const sortedTeams = response.teams.sort((a, b) => 
          (a.team_name || '').localeCompare(b.team_name || '')
        );
        setAllTeams(sortedTeams);
        console.log('✅ Loaded teams:', sortedTeams);
      }
    } catch (err) {
      console.error('Error loading teams:', err);
      setError('Failed to load teams');
    } finally {
      setTeamsLoading(false);
    }
  };

  const loadPicks = async (teamId) => {
    try {
      setLoading(true);
      setError(null);

      if (!teamId) {
        setPicks([]);
        setLoading(false);
        return;
      }

      console.log('🔍 Loading picks for team:', teamId);
      // §4.5.24: request only tradeable picks — backend applies the window +
      // finalizing freeze + not-consumed + owned predicate server-side.
      const response = await leaguesAPI.getTeamDraftPicks(leagueId, teamId, true);
      console.log('📦 API Response:', response);
      
      if (response.success) {
        console.log('✅ Raw picks from API:', response.picks);
        console.log('📅 Max tradeable year:', maxTradeableYear);
        
        // Transform nested structure to flat structure for frontend compatibility
        const transformedPicks = response.picks.map(pick => {
          const pickData = pick.pick_data || pick; // Handle both nested and flat structures
          
          return {
            pick_id: pickData.pick_id,
            pick_type: pickData.pick_type,
            year: pickData.year,
            round: pickData.round,
            overall_pick_number: pickData.overall_pick_number,
            pick_in_round: pickData.pick_in_round,
            // Flatten nested owner objects
            original_owner_team_id: pickData.original_owner?.team_id || pickData.original_owner_team_id,
            current_owner_team_id: pickData.current_owner?.team_id || pickData.current_owner_team_id,
            original_team_name: pickData.original_owner?.team_name || pickData.original_team_name,
            current_team_name: pickData.current_owner?.team_name || pickData.current_team_name,
            // Trade status
            is_traded: pickData.is_traded || false,
            was_traded: pickData.is_traded || false,
            // Ownership comes from the backend now (§4.5.24) — do NOT synthesize.
            is_owned: pickData.is_owned,
            is_original: pickData.is_original,
            is_consumed: pickData.is_consumed || false,
            display_label: pickData.display_label,
            // Selection info
            selection: pickData.selection,
            // Other fields
            is_lottery_pick: pickData.is_lottery_pick || false,
            trade_id: pickData.trade_id,
            created_at: pickData.created_at,
            updated_at: pickData.updated_at
          };
        });
        
        // Client-side defense-in-depth (§4.5.24): the backend already returns
        // only tradeable picks (tradeable_only=true), but re-assert the full
        // predicate — owned, unconsumed, and within the lower+upper bound
        // (currentYear < year <= maxTradeableYear). The lower bound is the
        // piece the old code lacked, which let current/past-season picks show.
        const tradeablePicks = transformedPicks.filter(p => {
          const isOwned = p.is_owned === true;
          const notConsumed = !p.is_consumed;
          const inRange = p.year > currentYear && p.year <= maxTradeableYear;
          console.log(`Pick ${p.year} R${p.round}: owned=${isOwned}, notConsumed=${notConsumed}, inRange=${inRange}`);
          return isOwned && notConsumed && inRange;
        });
        
        console.log('✅ Filtered tradeable picks:', tradeablePicks);
        setPicks(tradeablePicks);
      } else {
        console.error('❌ API returned error:', response);
        setError('Failed to load draft picks');
      }
    } catch (err) {
      console.error('Error loading draft picks:', err);
      setError(err.message || 'Failed to load draft picks');
    } finally {
      setLoading(false);
    }
  };

  // Split regular vs ML picks
  const regularPicks = picks.filter(p => p.pick_type !== 'ml');
  const mlPicks = picks.filter(p => p.pick_type === 'ml');

  // Group regular picks by year
  const picksByYear = regularPicks.reduce((acc, pick) => {
    const year = pick.year;
    if (!acc[year]) acc[year] = [];
    acc[year].push(pick);
    return acc;
  }, {});

  // Group ML picks by year
  const mlPicksByYear = mlPicks.reduce((acc, pick) => {
    const year = pick.year;
    if (!acc[year]) acc[year] = [];
    acc[year].push(pick);
    return acc;
  }, {});

  // Sort years ascending
  const sortedYears = Object.keys(picksByYear).sort((a, b) => parseInt(a) - parseInt(b));

  // Get pick value tier (for visual indicators)
  const getPickValueTier = (round) => {
    if (round <= 3) return 'elite';
    if (round <= 8) return 'high';
    if (round <= 15) return 'mid';
    return 'late';
  };

  const getPickValueColor = (round) => {
    const tier = getPickValueTier(round);
    switch (tier) {
      case 'elite': return 'bg-yellow-500';
      case 'high': return 'bg-orange-500';
      case 'mid': return 'bg-blue-500';
      case 'late': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getPickValueLabel = (round) => {
    const tier = getPickValueTier(round);
    switch (tier) {
      case 'elite': return 'Elite Value';
      case 'high': return 'High Value';
      case 'mid': return 'Mid Value';
      case 'late': return 'Late Round';
      default: return '';
    }
  };

  if (teamsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-teal-400" />
        <span className="ml-3 text-gray-300">Loading teams...</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-teal-400" />
        <span className="ml-3 text-gray-300">Loading draft picks...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg border border-red-500/50 ${dynastyTheme.classes.bg.darkCard}`}>
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  // Get selected team details
  const selectedTeam = allTeams.find(t => t.team_id === selectedTeamId);
  const isViewingOwnTeam = selectedTeamId === userTeam?.team_id;

  if (picks.length === 0) {
    return (
      <div>
        {/* Team Selector */}
        <div className={`mb-6 p-4 rounded-lg ${dynastyTheme.classes.bg.darkCard} border ${dynastyTheme.classes.border.neutral}`}>
          <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>
            View Team's Draft Picks
          </label>
          <select
            value={selectedTeamId || ''}
            onChange={(e) => setSelectedTeamId(e.target.value)}
            className={`
              w-full px-4 py-2 rounded-lg
              ${dynastyTheme.classes.bg.darkLighter}
              ${dynastyTheme.classes.text.white}
              border ${dynastyTheme.classes.border.neutral}
              focus:outline-none focus:border-teal-500
            `}
          >
            <option value="">Select a team...</option>
            {allTeams.map(team => (
              <option key={team.team_id} value={team.team_id}>
                {team.team_name} {team.team_id === userTeam?.team_id ? '(Your Team)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Empty State */}
        <div className={`${dynastyTheme.classes.text.neutralLight} text-center py-12`}>
          <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">
            {selectedTeamId ? 'No tradeable draft picks' : 'Select a team to view draft picks'}
          </p>
          <p className={`text-sm mt-2 ${dynastyTheme.classes.text.neutralLighter}`}>
            {selectedTeamId 
              ? `${selectedTeam?.team_name || 'This team'} has no tradeable draft picks`
              : 'Choose a team from the dropdown to see their available picks'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Team Selector */}
      <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkCard} border ${dynastyTheme.classes.border.neutral}`}>
        <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>
          View Team's Draft Picks
        </label>
        <select
          value={selectedTeamId || ''}
          onChange={(e) => setSelectedTeamId(e.target.value)}
          className={`
            w-full px-4 py-2 rounded-lg
            ${dynastyTheme.classes.bg.darkLighter}
            ${dynastyTheme.classes.text.white}
            border ${dynastyTheme.classes.border.neutral}
            focus:outline-none focus:border-teal-500
          `}
        >
          {allTeams.map(team => (
            <option key={team.team_id} value={team.team_id}>
              {team.team_name} {team.team_id === userTeam?.team_id ? '(Your Team)' : ''}
            </option>
          ))}
        </select>
        {!isViewingOwnTeam && selectedTeam && (
          <p className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-2`}>
            📋 Viewing {selectedTeam.team_name}'s draft picks
          </p>
        )}
      </div>

      {/* Selected Picks Summary */}
      {selectedPicks.length > 0 && (
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.primaryLight}/20 border ${dynastyTheme.classes.border.primary}`}>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
            <span className={`font-bold ${dynastyTheme.classes.text.white}`}>
              {selectedPicks.length} pick{selectedPicks.length !== 1 ? 's' : ''} selected for trade
            </span>
          </div>
          <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
            Go to "Propose Trade" tab to complete your trade offer
          </p>
        </div>
      )}

      {/* Picks Grouped by Year */}
      {sortedYears.map(year => {
        const yearPicks = picksByYear[year].sort((a, b) => a.round - b.round);
        
        return (
          <div key={year} className={`rounded-lg border ${dynastyTheme.classes.border.neutral} overflow-hidden`}>
            {/* Year Header */}
            <div className={`p-3 ${dynastyTheme.classes.bg.darkCard} border-b ${dynastyTheme.classes.border.neutral}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
                  <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                    {year} Draft
                  </h3>
                </div>
                <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                  {yearPicks.length} pick{yearPicks.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Picks Grid */}
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {yearPicks.map(pick => {
                const isSelected = selectedPicks.some(p => p.pick_id === pick.pick_id);
                const isTraded = pick.current_owner_team_id !== pick.original_owner_team_id;
                
                const CardTag = readOnly ? 'div' : 'button';
                return (
                  <CardTag
                    key={pick.pick_id}
                    onClick={readOnly ? undefined : () => onPickToggle(pick)}
                    className={`
                      p-4 rounded-lg text-left ${dynastyTheme.classes.transition}
                      border-2
                      ${readOnly ? 'cursor-default' : 'cursor-pointer'}
                      ${isSelected
                        ? `${dynastyTheme.classes.bg.primaryLight} ${dynastyTheme.classes.border.primary} shadow-lg`
                        : `${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.border.neutral} ${!readOnly ? 'hover:border-teal-500/50' : ''}`
                      }
                    `}
                  >
                    <div className="flex items-start justify-between mb-3">
                      {/* Round Badge */}
                      <div className={`
                        px-3 py-1 rounded-full text-xs font-bold text-white
                        ${getPickValueColor(pick.round)}
                      `}>
                        Round {pick.round}
                      </div>

                      {/* Selection Indicator — hidden in readOnly */}
                      {!readOnly && (
                        isSelected ? (
                          <CheckCircle className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
                        ) : (
                          <Circle className={`w-5 h-5 ${dynastyTheme.classes.text.neutralLight}`} />
                        )
                      )}
                    </div>

                    {/* Pick Value */}
                    <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                      {getPickValueLabel(pick.round)}
                    </div>

                    {/* Pick Origin (if traded) */}
                    {isTraded && (
                      <div className={`
                        mt-2 pt-2 border-t ${dynastyTheme.classes.border.neutral}
                        flex items-center gap-2
                      `}>
                        <ArrowRightLeft className="w-3 h-3 text-orange-400" />
                        <span className="text-xs text-orange-400 font-medium">
                          from {pick.original_team_name || 'Another Team'}
                        </span>
                      </div>
                    )}

                    {/* Original Owner (if current owner) */}
                    {!isTraded && (
                      <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-2`}>
                        Original pick
                      </div>
                    )}
                  </CardTag>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Minor League Picks */}
      {mlPicks.length > 0 && (
        <div className="mt-6">
          <h4 className={`text-sm font-bold ${dynastyTheme.classes.text.white} mb-3`}>
            Minor League Picks
          </h4>
          {Object.entries(mlPicksByYear).sort(([a], [b]) => Number(a) - Number(b)).map(([year, yearPicks]) => {
            const sortedYearPicks = yearPicks.sort((a, b) => a.round - b.round);
            return (
              <div key={year} className={`rounded-lg border ${dynastyTheme.classes.border.neutral} overflow-hidden mb-3`}>
                {/* Year Header */}
                <div className={`p-3 ${dynastyTheme.classes.bg.darkCard} border-b ${dynastyTheme.classes.border.neutral}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
                      <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                        {year} ML Draft
                      </h3>
                    </div>
                    <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                      {sortedYearPicks.length} pick{sortedYearPicks.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {/* Picks Grid */}
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {sortedYearPicks.map(pick => {
                    const isSelected = selectedPicks.some(p => p.pick_id === pick.pick_id);
                    const isTraded = pick.current_owner_team_id !== pick.original_owner_team_id;

                    const MLCardTag = readOnly ? 'div' : 'button';
                    return (
                      <MLCardTag
                        key={pick.pick_id}
                        onClick={readOnly ? undefined : () => onPickToggle(pick)}
                        className={`
                          p-4 rounded-lg text-left ${dynastyTheme.classes.transition}
                          border-2
                          ${readOnly ? 'cursor-default' : 'cursor-pointer'}
                          ${isSelected
                            ? `${dynastyTheme.classes.bg.primaryLight} ${dynastyTheme.classes.border.primary} shadow-lg`
                            : `${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.border.neutral} ${!readOnly ? 'hover:border-teal-500/50' : ''}`
                          }
                        `}
                      >
                        <div className="flex items-start justify-between mb-3">
                          {/* Round Badge */}
                          <div className={`
                            px-3 py-1 rounded-full text-xs font-bold text-white
                            ${getPickValueColor(pick.round)}
                          `}>
                            Round {pick.round}
                          </div>

                          {/* Selection Indicator — hidden in readOnly */}
                          {!readOnly && (
                            isSelected ? (
                              <CheckCircle className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
                            ) : (
                              <Circle className={`w-5 h-5 ${dynastyTheme.classes.text.neutralLight}`} />
                            )
                          )}
                        </div>

                        {/* Pick Value */}
                        <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                          {getPickValueLabel(pick.round)}
                        </div>

                        {/* Pick Origin (if traded) */}
                        {isTraded && (
                          <div className={`
                            mt-2 pt-2 border-t ${dynastyTheme.classes.border.neutral}
                            flex items-center gap-2
                          `}>
                            <ArrowRightLeft className="w-3 h-3 text-orange-400" />
                            <span className="text-xs text-orange-400 font-medium">
                              from {pick.original_team_name}
                            </span>
                          </div>
                        )}

                        {/* Original Owner (if current owner) */}
                        {!isTraded && (
                          <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-2`}>
                            Original pick
                          </div>
                        )}
                      </MLCardTag>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Help Text — hidden in readOnly */}
      {!readOnly && (
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkCard} border ${dynastyTheme.classes.border.neutral}`}>
          <div className="flex items-start gap-3">
            <TrendingUp className={`w-5 h-5 ${dynastyTheme.classes.text.primary} flex-shrink-0 mt-0.5`} />
            <div>
              <p className={`text-sm ${dynastyTheme.classes.text.white} font-medium mb-1`}>
                How to trade picks
              </p>
              <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                Click any pick to select it for trading. Selected picks will be added to your trade offer.
                Go to the "Propose Trade" tab to complete your trade.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftPicksTab;
